<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import { formatToInternational } from '@shared/utils'

const MAX_DECIMAL_DIGITS = 2

export type AmountPreset = number | { value: number; label: string }

const props = withDefaults(
  defineProps<{
    modelValue: number
    presets?: AmountPreset[]
    step?: number
    min?: number
    max?: number
  }>(),
  {
    // eslint-disable-next-line no-magic-numbers -- AED preset amounts are the canonical product values
    presets: () => [2000, 5000, 10000, 25000],

    step: 1000,
    min: 0,
    max: Number.POSITIVE_INFINITY,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', val: number): void
}>()

function formatNumber(amount: number): string {
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: MAX_DECIMAL_DIGITS,
  })
}

function clamp(value: number): number {
  if (isNaN(value)) return props.min
  return Math.min(Math.max(value, props.min), props.max)
}

const inputValue = ref(formatNumber(props.modelValue))
const typing = ref(false)

watch(
  () => props.modelValue,
  (val) => {
    if (!typing.value) inputValue.value = formatNumber(val)
  },
)

watch(
  () => props.max,
  (newMax) => {
    if (props.modelValue > newMax) {
      const clamped = clamp(props.modelValue)
      emit('update:modelValue', clamped)
      if (!typing.value) inputValue.value = formatNumber(clamped)
    }
  },
)

const setAmount = (val: number) => {
  const clamped = clamp(val)
  emit('update:modelValue', clamped)
  inputValue.value = formatNumber(clamped)
}

const increment = () => setAmount(props.modelValue + props.step)
const decrement = () => {
  if (props.modelValue > props.min) setAmount(Math.max(props.min, props.modelValue - props.step))
}

const onInput = (event: Event) => {
  typing.value = true
  const target = event.target as HTMLInputElement
  const raw = target.value.replace(/,/g, '')
  const parsed = parseFloat(raw)
  if (isNaN(parsed)) {
    inputValue.value = target.value
    emit('update:modelValue', 0)
    return
  }
  const clamped = clamp(parsed)
  emit('update:modelValue', clamped)
  // If the typed value got clamped, mirror it in the input so users see the cap.
  inputValue.value = clamped !== parsed ? formatNumber(clamped) : target.value
}

const onBlur = () => {
  typing.value = false
  inputValue.value = formatNumber(props.modelValue)
}

const presetValue = (preset: AmountPreset): number =>
  typeof preset === 'number' ? preset : preset.value

const presetLabel = (preset: AmountPreset): string =>
  typeof preset === 'number' ? formatToInternational(preset) : preset.label

const activePreset = computed(
  () => props.presets.find((preset) => presetValue(preset) === props.modelValue) ?? null,
)
</script>

<template>
  <Box direction="col" gap="3" class="aif">
    <Box align="center" justify="between" class="aif__input">
      <Box align="center" gap="2" grow class="aif__input-left">
        <Text type="title-lg" color="muted" is="span" class="aif__currency">AED</Text>
        <!-- Native <input type="text"> with custom typography — promote to <NumericInput> kit
             element if a 5+-caller numeric input pattern emerges. §18 exception today. -->
        <input
          :value="inputValue"
          class="aif__amount"
          type="text"
          inputmode="numeric"
          @input="onInput"
          @blur="onBlur"
        />
      </Box>
      <Box direction="col" gap="1" class="aif__stepper">
        <Box
          is="button"
          flex
          align="center"
          justify="center"
          class="aif__step-btn"
          type="button"
          aria-label="Increase amount"
          @click="increment"
        >
          <PiIcon name="chevron-up" style="font-size: 12px; color: var(--p-text-muted-color)" />
        </Box>
        <Box
          is="button"
          flex
          align="center"
          justify="center"
          class="aif__step-btn"
          type="button"
          aria-label="Decrease amount"
          @click="decrement"
        >
          <PiIcon name="chevron-down" style="font-size: 12px; color: var(--p-text-muted-color)" />
        </Box>
      </Box>
    </Box>

    <Box v-if="presets.length" gap="2" class="aif__presets">
      <Box
        v-for="preset in presets"
        :key="presetValue(preset)"
        is="button"
        class="aif__preset"
        :class="{ 'aif__preset--active': activePreset === preset }"
        type="button"
        :aria-label="`Set amount to AED ${presetValue(preset)}`"
        :aria-pressed="activePreset === preset"
        @click="setAmount(presetValue(preset))"
      >
        <Text type="body" weight="medium" is="span">{{ presetLabel(preset) }}</Text>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.aif__input {
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  height: 68px;
  padding: 0.625rem 0.875rem;
  box-sizing: border-box;
}

.aif__input-left {
  min-width: 0;
}

.aif__currency {
  line-height: 30px;
  flex-shrink: 0;
}

.aif__amount {
  border: none;
  outline: none;
  background: transparent;
  font-size: 20px;
  font-weight: 700;
  color: var(--p-text-color);
  line-height: 30px;
  width: 0;
  flex: 1;
  min-width: 0;
  -moz-appearance: textfield;
  appearance: textfield;
}

.aif__amount::-webkit-outer-spin-button,
.aif__amount::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.aif__stepper {
  flex-shrink: 0;
  margin-left: 8px;
}

.aif__step-btn {
  width: 20px;
  height: 20px;
  background: var(--p-surface-100);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s;
}

.aif__step-btn:hover {
  background: var(--p-surface-200);
}

.aif__step-btn:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.aif__preset {
  flex: 1;
  height: 38px;
  background: var(--p-surface-100);
  border: 1px solid var(--p-content-border-color);
  border-radius: 10px;
  color: var(--p-text-muted-color);
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;
}

.aif__preset:hover {
  background: var(--p-primary-50);
  border-color: var(--p-primary-200);
}

.aif__preset:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.aif__preset--active {
  background: var(--p-primary-100);
  border-color: var(--p-primary-600);
  color: var(--p-primary-900);
}
</style>
